import {
    Autocomplete,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { downloadUTF8, removeToolId } from "./utils";
import { useFetch, useSnackbar } from "../../hooks";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BACKEND_ROUTES } from "../../backendRoutes";
import PropTypes from "prop-types";
import slugify from "slugify";
import { useLiteralRequests } from "./hooks";
import { useParams } from "react-router-dom";

ParcellaireMetadataForm.propTypes = {
    parcellaireState: PropTypes.object.isRequired,
    setIsOpen: PropTypes.func.isRequired,
};

export function ParcellaireMetadataForm({ parcellaireState, setIsOpen }) {
    const { get, post } = useFetch();
    const { siteUuid } = useParams();
    const { openSnackbar } = useSnackbar();

    // Loading state and errors are handled in the parent component
    const [{ data: site }, { data: literalPipelineTemplates }] =
        useLiteralRequests();

    const { control, handleSubmit } = useForm({
        defaultValues: {
            pipelineTemplate:
                literalPipelineTemplates.length === 1
                    ? literalPipelineTemplates[0]
                    : null,
        },
    });

    const onSubmit = async (payload) => {
        try {
            const savedParcellaire = removeToolId(
                parcellaireState.transformedGeoJson
            );

            const experimentFetchResult = await get(
                `${BACKEND_ROUTES.EXPERIMENT}?siteUuid=${siteUuid}&name=${site.name}&cropUuid=${payload.pipelineTemplate.cropUuid}`
            );

            let experiment;

            if (experimentFetchResult.count === 0) {
                experiment = await post(
                    `${BACKEND_ROUTES.SITE}/${siteUuid}/experiments`,
                    {
                        body: {
                            cropUuid: payload.pipelineTemplate.cropUuid,
                            name: site.name,
                        },
                    }
                );
            } else {
                experiment = experimentFetchResult.rows[0];
            }

            savedParcellaire.metadata = {
                hcc_pt_uuid: payload.pipelineTemplate.uuid,
                hcc_site_uuid: siteUuid,
                cloverfield_site_uuid: site.cloverfieldUuid,
                field: slugify(site.name, "_"),
                experiment: slugify(site.name, "_"),
                experimentId: experiment.uuid,
                campaign: slugify(site.Contract.name, "_"),
                nationalInfrastructure: slugify(
                    site.Contract.Company.name,
                    "_"
                ),
                CropId: payload.pipelineTemplate.cropUuid,
            };

            downloadUTF8("plotmap.geojson", JSON.stringify(savedParcellaire));

            setIsOpen(false);
        } catch (error) {
            openSnackbar(
                "Something went wrong. Please try again later.",
                "error"
            );
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>Export plot map</DialogTitle>
            <Divider variant="middle" />
            <DialogContent>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={12}>
                        <Controller
                            control={control}
                            name="pipelineTemplate"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="pipelineTemplate"
                                    options={literalPipelineTemplates}
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, value) => {
                                        onChange(value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Pipeline Template"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={() => setIsOpen(false)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    type="submit"
                >
                    Export
                </Button>
            </DialogActions>
        </form>
    );
}
